import { PatientInfoDrawer, StudyInfoDrawer } from '@/entities/study';
import { PDFLoading } from '@/shared/ui/components';
import { InboxECGViewer } from '@/widgets/ecg-viewer';
import { STATUS_REPORT } from '@/shared/config';
import {
  arrowDownGreyIcon,
  arrowDownWhiteIcon,
  loadingGreyIcon,
  loadingWhiteIcon,
} from '@/shared/ui/assets';
import { auth } from '@/shared/lib';
import { Spin } from 'antd';
import classNames from 'classnames';
import _ from 'lodash';
import { t } from 'ttag';
import { generateTitleBlockArrayReportDetails } from '../lib/inboxDetails';
import { useInboxDetailsPage } from './useInboxDetailsPage';
import InboxDetails from './inboxDetails';

/**
 * Renders the inbox report details page with its study and patient info drawers and the inline ECG viewer. Shows the PDF loading screen with download progress while the report detail is loading or empty, otherwise the report details with generated title blocks, the per-report status icon resolver, and the hook's handlers for going back, changing status, opening the ECG viewer and reacting to a report being read, saved or verified.
 *
 * @returns The inbox details page markup, or the loading screen.
 */
const InboxDetailsPage = () => {
  const {
    state,
    isStudyManagementPage,
    reportData,
    isLoadingReportDetail,
    isInstantReport,
    hasVerifiedReport,
    handleGoBack,
    handleClickViewEcgViewer,
    toggleInboxECGViewer,
    toggleDisplayDrawer,
    updateClickedReport,
    onChangeStatusReportDetail,
    afterCloseInstantReportAlert,
    handleViewVerifiedReportClick,
    handleAfterReadingReport,
    handleAfterSavingPdfReport,
  } = useInboxDetailsPage();

  /**
   * Chooses the trailing icon for a report row's status control. Renders a spinner, white when the pending status is COMPLETED and grey otherwise, while that report appears in state.listChangingStatusReport with isChangingStatus, and otherwise a white or grey arrow-down icon depending on whether statusReport is COMPLETED.
   *
   * @param report - Report whose status control is being rendered.
   * @returns Icon or spinner element for the status control.
   */
  const getSuffixIcon = (report: any) => {
    /**
     * Holds the entry from state.listChangingStatusReport whose id matches the current report. Found with a lodash lookup over the in-progress status-change list, and undefined when no status change is pending for that report.
     *
     * @returns Pending status-change entry, or undefined.
     */
    const updatingReport = _.find(state.listChangingStatusReport, (item) => item.id === report.id);
    if (updatingReport?.isChangingStatus) {
      if (updatingReport.status === STATUS_REPORT.COMPLETED) {
        return (
          <Spin
            indicator={
              <img src={loadingWhiteIcon} alt="" style={{ animation: 'spin 1s linear infinite' }} />
            }
          />
        );
      }
      return (
        <Spin
          indicator={
            <img src={loadingGreyIcon} alt="" style={{ animation: 'spin 1s linear infinite' }} />
          }
        />
      );
    }
    if (report.statusReport === STATUS_REPORT.COMPLETED) {
      return <img src={arrowDownWhiteIcon} alt="" />;
    }
    return <img src={arrowDownGreyIcon} alt="" />;
  };

  if (isLoadingReportDetail || _.isEmpty(reportData)) {
    return (
      <div
        className={classNames(
          'custom-page inbox-page inbox-details-page',
          { 'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner() },
          { 'has-login-banner': state.hasLoginBanner },
        )}
      >
        <PDFLoading
          downloadProgress={state.downloadProgress}
          downloadEstimated={state.downloadEstimated ?? undefined}
        />
      </div>
    );
  }

  return (
    <>
      <StudyInfoDrawer
        visible={state.isVisibleStudyInfoDrawer}
        fetchStudyId={state.clickedData?.study?.id}
        handleClickCloseButton={() => toggleDisplayDrawer()}
      />
      <PatientInfoDrawer
        visible={state.isVisiblePatientInfoDrawer}
        title={t`Patient information`}
        studyId={state.clickedData?.study?.id}
        handleClickCloseButton={() => toggleDisplayDrawer()}
      />

      <div
        className={classNames(
          'custom-page inbox-page inbox-details-page',
          { 'has-marketing-banner': auth.hasMarketingBanner() || auth.hasLogoBanner() },
          { 'has-login-banner': state.hasLoginBanner },
        )}
      >
        {!!state.inboxECGViewerData && (
          <InboxECGViewer data={state.inboxECGViewerData} handleClickBack={toggleInboxECGViewer} />
        )}
        <InboxDetails
          data={reportData}
          titleBlockArray={generateTitleBlockArrayReportDetails(reportData)}
          handleClickBack={() => handleGoBack(reportData)}
          updateClickedReport={updateClickedReport}
          updateStatusReport={onChangeStatusReportDetail}
          getSuffixIcon={getSuffixIcon}
          handleClickViewEcgViewer={handleClickViewEcgViewer}
          toggleDisplayDrawer={toggleDisplayDrawer}
          handleAfterReadingReport={handleAfterReadingReport}
          handleAfterSavingPdfReport={handleAfterSavingPdfReport}
          isInstantReport={isInstantReport}
          isStudyManagementPage={isStudyManagementPage}
          hasClosedInstantReportAlert={state.hasClosedInstantReportAlert}
          afterCloseInstantReportAlert={afterCloseInstantReportAlert}
          hasVerifiedReport={hasVerifiedReport}
          handleViewVerifiedReportClick={handleViewVerifiedReportClick}
        />
      </div>
    </>
  );
};

export default InboxDetailsPage;
